import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  MessageSquare,
  X,
  Check,
  CheckCheck,
  ChevronLeft,
  Search,
  CheckCircle,
  AlertCircle,
  DollarSign,
} from 'lucide-react';
import { MessageService } from '../../services/api/messageService';
import { NotificationService, Notification } from '../../services/api/notificationService';
import { useAppContext } from '../../context/AppContextCompat';
import {
  filterCustomerNotifications,
  getCustomerNotificationIconBg,
  getCustomerNotificationTypeLabel,
  groupCustomerNotificationsByDate,
} from '../../utils/customerNotificationList';
import { isNativeUi } from '../../utils/nativeUi';
import { useMediaQuery } from '../../hooks/useMediaQuery';

interface MessageNotificationsProps {
  className?: string;
}

const CustomerBadgeIcon: React.FC<{ type: string; className?: string }> = ({
  type,
  className = 'h-3 w-3 text-white',
}) => {
  const cls = className;
  switch (type) {
    case 'message':
    case 'message_new':
      return <MessageSquare className={cls} />;
    case 'job_status':
    case 'job_new':
    case 'new_recommended_case':
      return <CheckCircle className={cls} />;
    case 'payment':
    case 'payment_received':
    case 'payment_pending':
      return <DollarSign className={cls} />;
    case 'review_new':
    case 'review_reply':
      return <Bell className={cls} />;
    case 'system':
      return <AlertCircle className={cls} />;
    default:
      return <Bell className={cls} />;
  }
};

const MessageNotifications: React.FC<MessageNotificationsProps> = ({ className = '' }) => {
  const { currentUser } = useAppContext();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  /** 客戶、公司、工匠共用同一套頂欄通知下拉（篩選、分組、列表樣式） */
  const useRichNotificationPanel =
    currentUser?.userType === 'customer' ||
    currentUser?.userType === 'company' ||
    currentUser?.userType === 'craftsman';
  const [customerFilter, setCustomerFilter] = useState<'all' | 'unread' | 'read'>('all');
  const [customerCategory, setCustomerCategory] = useState('all');
  const [customerSearch, setCustomerSearch] = useState('');

  const isPhoneWidth = useMediaQuery('(max-width: 767px)');
  const appStyleUi = useMemo(() => isNativeUi(), []) || isPhoneWidth;
  const appNotificationPanel = appStyleUi && useRichNotificationPanel;

  const fetchUnreadCount = async () => {
    if (!currentUser) return;

    try {
      const messageCount = await MessageService.getUnreadCount(currentUser.id);
      const notificationCount = await NotificationService.getUnreadCount(currentUser.id);
      setUnreadCount(messageCount + notificationCount);
    } catch (error) {
      console.error('獲取未讀數量失敗:', error);
    }
  };

  const fetchNotifications = useCallback(async () => {
    if (!currentUser) return;

    try {
      setIsLoading(true);
      const limit =
        currentUser.userType === 'customer' ||
        currentUser.userType === 'company' ||
        currentUser.userType === 'craftsman'
          ? 50
          : 10;
      const latestNotifications = await NotificationService.getUserNotifications(currentUser.id, limit);
      setNotifications(latestNotifications);
    } catch (error) {
      console.error('獲取通知失敗:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser]);

  const markAsRead = async (notificationId: string) => {
    try {
      await NotificationService.markAsRead([notificationId]);
      setNotifications((prev) => prev.map((n) => (n.id === notificationId ? { ...n, isRead: true } : n)));
      await fetchUnreadCount();
    } catch (error) {
      console.error('標記為已讀失敗:', error);
    }
  };

  const markAllAsRead = async () => {
    if (!currentUser) return;

    try {
      await NotificationService.markAllAsRead(currentUser.id);
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      await fetchUnreadCount();
    } catch (error) {
      console.error('標記所有為已讀失敗:', error);
    }
  };

  const deleteNotification = async (notificationId: string) => {
    try {
      await NotificationService.deleteNotification(notificationId);
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      await fetchUnreadCount();
    } catch (error) {
      console.error('刪除通知失敗:', error);
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.isRead) {
      await markAsRead(notification.id);
    }

    setShowDropdown(false);

    if (!notification.relatedId) {
      return;
    }

    try {
      const userType = currentUser?.userType;

      switch (notification.type) {
        case 'new_recommended_case':
          if (userType === 'company') {
            navigate(`/company/jobs?highlight=${notification.relatedId}&tab=recommended`);
          }
          break;

        case 'job_new':
          if (userType === 'company') {
            navigate(`/company/jobs?highlight=${notification.relatedId}&status=pending`);
          } else if (userType === 'craftsman') {
            navigate(`/craftsman/jobs?tab=pending&highlight=${notification.relatedId}`);
          }
          break;

        case 'message':
        case 'message_new':
          navigate(`/messages?serviceRequestId=${notification.relatedId}`);
          break;

        case 'payment_received':
        case 'payment_pending':
          if (userType === 'company') {
            navigate('/company/earnings');
          } else if (userType === 'craftsman') {
            navigate('/craftsman/earnings');
          } else if (userType === 'customer') {
            navigate(`/service-history?highlight=${notification.relatedId}`);
          }
          break;

        case 'review_new':
        case 'review_reply':
          if (userType === 'company') {
            navigate('/company/reviews');
          } else if (userType === 'craftsman') {
            navigate('/craftsman/reviews');
          }
          break;

        case 'verification_approved':
        case 'verification_disputed':
        case 'verification_admin_followup':
        case 'verification_dispute_mediated':
          if (userType === 'craftsman') {
            navigate(`/craftsman/jobs?tab=active&highlight=${notification.relatedId}`);
          } else if (userType === 'customer') {
            navigate(`/service-history?highlight=${notification.relatedId}`);
          } else if (userType === 'company') {
            navigate(`/company/jobs?highlight=${notification.relatedId}`);
          }
          break;

        default:
          if (userType === 'company') {
            navigate('/company/jobs');
          } else if (userType === 'craftsman') {
            navigate('/craftsman/jobs');
          } else if (userType === 'customer') {
            navigate('/customer/notifications');
          }
          console.log('未处理的通知类型:', notification.type, 'relatedId:', notification.relatedId);
      }
    } catch (error) {
      console.error('跳转失败:', error);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'message':
        return <MessageSquare className="w-4 h-4 text-blue-500" />;
      case 'job_status':
        return <Bell className="w-4 h-4 text-green-500" />;
      case 'system':
        return <Bell className="w-4 h-4 text-gray-500" />;
      default:
        return <Bell className="w-4 h-4 text-gray-500" />;
    }
  };

  const formatTime = (date: Date | string) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diff = now.getTime() - dateObj.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return '剛剛';
    if (minutes < 60) return `${minutes}分鐘前`;
    if (hours < 24) return `${hours}小時前`;
    if (days < 7) return `${days}天前`;
    return dateObj.toLocaleDateString();
  };

  const customerCategories = useMemo(
    () => Array.from(new Set(notifications.map((n) => n.type).filter(Boolean))),
    [notifications]
  );

  const customerFiltered = useMemo(
    () =>
      useRichNotificationPanel
        ? filterCustomerNotifications(
            notifications,
            customerFilter,
            customerCategory,
            customerSearch
          )
        : notifications,
    [
      useRichNotificationPanel,
      notifications,
      customerFilter,
      customerCategory,
      customerSearch,
    ]
  );

  const customerGrouped = useMemo(
    () =>
      useRichNotificationPanel ? groupCustomerNotificationsByDate(customerFiltered) : [],
    [useRichNotificationPanel, customerFiltered]
  );

  const resetCustomerFilters = () => {
    setCustomerFilter('all');
    setCustomerCategory('all');
    setCustomerSearch('');
  };

  useEffect(() => {
    if (!currentUser) return;

    fetchUnreadCount();
    fetchNotifications();

    const unsubscribe = NotificationService.subscribeToNotifications(currentUser.id, () => {
      fetchUnreadCount();
      if (showDropdown) {
        fetchNotifications();
      }
    });

    return () => {
      unsubscribe();
    };
  }, [currentUser, showDropdown, fetchNotifications]);

  useEffect(() => {
    NotificationService.requestNotificationPermission();
  }, []);

  useEffect(() => {
    if (!showDropdown || !appNotificationPanel) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showDropdown, appNotificationPanel]);

  const appUnreadNotificationCount = useMemo(
    () => notifications.filter((n) => !n.isRead).length,
    [notifications]
  );

  if (!currentUser) return null;

  const openNotificationCenter = () => {
    setShowDropdown(false);
    if (currentUser.userType === 'company') {
      navigate('/company/jobs');
    } else if (currentUser.userType === 'craftsman') {
      navigate('/craftsman/notifications');
    } else {
      navigate('/customer/notifications');
    }
  };

  const appPanel = (
    <AnimatePresence>
      {showDropdown && appNotificationPanel && (
        <motion.div
          key="app-notification-panel"
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'tween', duration: 0.25, ease: 'easeOut' }}
          className="fixed inset-0 z-[200] flex flex-col bg-[#f4f6fb] dark:bg-gray-900"
          role="dialog"
          aria-modal="true"
          aria-label="通知"
        >
          <div
            className="shrink-0 bg-white shadow-[0_1px_0_rgba(29,53,87,0.08)] dark:bg-gray-800"
            style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
          >
            <div className="flex h-14 items-center gap-1 px-2">
              <button
                type="button"
                onClick={() => setShowDropdown(false)}
                className="flex h-10 w-10 items-center justify-center rounded-full text-[#1d3557] hover:bg-[#eef2f8] dark:text-white dark:hover:bg-gray-700"
                aria-label="返回"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <h2 className="flex-1 text-lg font-bold text-[#1d3557] dark:text-white">通知</h2>
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={() => void markAllAsRead()}
                  className="mr-2 inline-flex items-center gap-1 rounded-full bg-[#eef2f8] px-3 py-1.5 text-xs font-semibold text-[#1d3557] dark:bg-gray-700 dark:text-gray-100"
                >
                  <CheckCheck className="h-3.5 w-3.5" />
                  全部已讀
                </button>
              )}
            </div>

            <div className="space-y-3 px-4 pb-3">
              <div
                role="tablist"
                aria-label="通知篩選"
                className="grid grid-cols-3 gap-1 rounded-full bg-[#eef2f8] p-1 dark:bg-gray-700"
              >
                {(
                  [
                    { key: 'all' as const, label: '全部' },
                    { key: 'unread' as const, label: '未讀' },
                    { key: 'read' as const, label: '已讀' },
                  ]
                ).map((tab) => {
                  const selected = customerFilter === tab.key;
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      role="tab"
                      aria-selected={selected}
                      onClick={() => setCustomerFilter(tab.key)}
                      className={`flex items-center justify-center gap-1 rounded-full py-2 text-sm font-semibold transition-colors ${
                        selected
                          ? 'bg-[#1d3557] text-white shadow-[0_4px_12px_rgba(29,53,87,0.25)]'
                          : 'text-[#274c77] dark:text-gray-200'
                      }`}
                    >
                      {tab.label}
                      {tab.key === 'unread' && appUnreadNotificationCount > 0 && (
                        <span
                          className={`min-w-[1.25rem] rounded-full px-1.5 text-[11px] leading-5 ${
                            selected ? 'bg-white/20 text-white' : 'bg-[#e63946] text-white'
                          }`}
                        >
                          {appUnreadNotificationCount > 99 ? '99+' : appUnreadNotificationCount}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="relative">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#274c77]/60" />
                <input
                  type="search"
                  placeholder="搜尋通知…"
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  className="w-full rounded-full border-0 bg-[#eef2f8] py-2.5 pl-10 pr-4 text-sm text-[#1d3557] placeholder:text-[#274c77]/50 focus:outline-none focus:ring-2 focus:ring-[#1d3557]/20 dark:bg-gray-700 dark:text-white"
                />
              </div>

              {customerCategories.length > 0 && (
                <div className="-mx-4 flex gap-2 overflow-x-auto px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                  {['all', ...customerCategories].map((cat) => {
                    const selected = customerCategory === cat;
                    return (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setCustomerCategory(cat)}
                        className={`shrink-0 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                          selected
                            ? 'border-[#1d3557] bg-[#1d3557] text-white'
                            : 'border-[#d9e1ee] bg-white text-[#274c77] dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200'
                        }`}
                      >
                        {cat === 'all' ? '全部類別' : getCustomerNotificationTypeLabel(cat)}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4">
            {isLoading && notifications.length === 0 ? (
              <div className="py-16 text-center">
                <div className="mx-auto h-7 w-7 animate-spin rounded-full border-2 border-[#1d3557]/20 border-t-[#1d3557]" />
                <p className="mt-3 text-sm text-gray-500">載入中…</p>
              </div>
            ) : customerFiltered.length === 0 ? (
              <div className="flex flex-col items-center px-6 py-16 text-center">
                <span className="flex h-20 w-20 items-center justify-center rounded-full bg-[#eef2f8] dark:bg-gray-800">
                  <Bell className="h-9 w-9 text-[#1d3557]/50 dark:text-gray-400" />
                </span>
                <p className="mt-4 text-base font-semibold text-[#1d3557] dark:text-white">暫無通知</p>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">目前沒有符合篩選的通知</p>
              </div>
            ) : (
              <div className="space-y-4">
                {customerGrouped.map((group) => (
                  <section key={group.label}>
                    <div className="mb-2 flex items-center justify-between px-1">
                      <span className="text-xs font-bold tracking-wide text-[#274c77]/70 dark:text-gray-400">
                        {group.label}
                      </span>
                      <button
                        type="button"
                        onClick={resetCustomerFilters}
                        className="text-xs font-semibold text-[#274c77] dark:text-blue-300"
                      >
                        查看全部
                      </button>
                    </div>
                    <ul className="divide-y divide-[#eef2f8] overflow-hidden rounded-[1.25rem] bg-white shadow-[0_8px_24px_rgba(29,53,87,0.08)] dark:divide-gray-700 dark:bg-gray-800">
                      {group.notifications.map((notification) => (
                        <li
                          key={notification.id}
                          className={`flex items-start gap-1 py-3 pl-3 pr-1.5 ${
                            notification.isRead ? '' : 'bg-[#f5f8fd] dark:bg-gray-700/40'
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => void handleNotificationClick(notification)}
                            className="flex min-w-0 flex-1 items-start gap-3 text-left"
                          >
                            <span
                              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${getCustomerNotificationIconBg(notification.type)}`}
                            >
                              <CustomerBadgeIcon type={notification.type} className="h-5 w-5 text-white" />
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="flex items-center gap-1.5">
                                <span
                                  className={`truncate text-sm text-[#1d3557] dark:text-white ${
                                    notification.isRead ? 'font-medium' : 'font-bold'
                                  }`}
                                >
                                  {notification.title}
                                </span>
                                {!notification.isRead && (
                                  <span className="h-2 w-2 shrink-0 rounded-full bg-[#e63946]" title="未讀" />
                                )}
                              </span>
                              {notification.message ? (
                                <span className="mt-0.5 line-clamp-2 block text-[13px] leading-snug text-gray-600 dark:text-gray-300">
                                  {notification.message}
                                </span>
                              ) : null}
                              <span className="mt-1 block text-[11px] text-gray-400">
                                {getCustomerNotificationTypeLabel(notification.type)} · {formatTime(notification.createdAt)}
                              </span>
                            </span>
                          </button>
                          <button
                            type="button"
                            onClick={() => void deleteNotification(notification.id)}
                            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-300 hover:bg-gray-100 hover:text-gray-500 dark:text-gray-500 dark:hover:bg-gray-700"
                            aria-label="移除"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
            )}
          </div>

          <div
            className="shrink-0 border-t border-[#eef2f8] bg-white px-4 pt-3 dark:border-gray-700 dark:bg-gray-800"
            style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}
          >
            <button
              type="button"
              onClick={openNotificationCenter}
              className="w-full rounded-full bg-[#1d3557] py-3 text-sm font-semibold text-white shadow-[0_8px_20px_rgba(29,53,87,0.25)] active:bg-[#274c77]"
            >
              {currentUser.userType === 'company' ? '前往案件管理' : '前往通知中心'}
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  /** 客戶：手機用 fixed 貼齊視窗，避免 absolute+right-0 在頂欄右側被裁切；md+ 維持下拉錨點 */
  const customerPanelLayout =
    'flex flex-col max-md:fixed max-md:left-2 max-md:right-2 max-md:top-[calc(4rem+env(safe-area-inset-top,0px)+8px)] max-md:z-[60] max-md:max-h-[min(88dvh,680px)] md:absolute md:right-0 md:top-full md:z-50 md:mt-2 md:max-h-[min(92vh,720px)] md:w-[min(100vw-2rem,28rem)]';

  const nonCustomerPanelLayout =
    'absolute right-0 top-full z-50 mt-2 max-h-96 w-[min(100vw-1.5rem,20rem)] max-md:w-auto sm:w-80 max-md:fixed max-md:left-2 max-md:right-2 max-md:top-[calc(4rem+env(safe-area-inset-top,0px)+8px)] max-md:z-[60] max-md:max-h-[min(85dvh,560px)]';

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => {
          setShowDropdown(!showDropdown);
          if (!showDropdown) {
            void fetchNotifications();
          }
        }}
        className="relative inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-lg text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 touch-manipulation dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white sm:min-h-0 sm:min-w-0 sm:p-2"
      >
        <Bell className="h-6 w-6" />
        {unreadCount > 0 && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-medium text-white"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </motion.span>
        )}
      </button>

      {createPortal(appPanel, document.body)}

      <AnimatePresence>
        {showDropdown && !appNotificationPanel && (
          <>
            <div
              className="fixed bottom-0 left-0 right-0 z-30 bg-black/10 dark:bg-black/30"
              style={{ top: 'calc(4rem + env(safe-area-inset-top, 0px))' }}
              onClick={() => setShowDropdown(false)}
              aria-hidden
            />

            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className={`flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-800 ${
                useRichNotificationPanel ? customerPanelLayout : nonCustomerPanelLayout
              }`}
            >
              <div className="flex items-center justify-between border-b border-gray-200 p-3 dark:border-gray-700">
                <h3 className="font-semibold text-gray-900 dark:text-white">通知</h3>
                <div className="flex items-center space-x-2">
                  {unreadCount > 0 && (
                    <button
                      type="button"
                      onClick={() => void markAllAsRead()}
                      className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      全部已讀
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowDropdown(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    aria-label="關閉"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {useRichNotificationPanel ? (
                <>
                  <div className="shrink-0 space-y-2 border-b border-gray-100 px-3 py-2 dark:border-gray-700 max-md:px-4">
                    <div className="flex flex-wrap gap-1.5">
                      {(
                        [
                          { key: 'all' as const, label: '全部' },
                          { key: 'unread' as const, label: '未讀' },
                          { key: 'read' as const, label: '已讀' },
                        ]
                      ).map((tab) => (
                        <button
                          key={tab.key}
                          type="button"
                          onClick={() => setCustomerFilter(tab.key)}
                          className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                            customerFilter === tab.key
                              ? 'bg-blue-100 text-blue-600'
                              : 'text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700'
                          }`}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </div>
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 md:left-2.5 md:h-3.5 md:w-3.5" />
                      <input
                        type="search"
                        placeholder="搜尋通知…"
                        value={customerSearch}
                        onChange={(e) => setCustomerSearch(e.target.value)}
                        className="w-full rounded-full border-0 bg-gray-100 py-2.5 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:bg-gray-700 dark:text-white md:py-2 md:pl-8 md:text-xs"
                      />
                    </div>
                    <div className="flex gap-1.5 overflow-x-auto pb-0.5">
                      <button
                        type="button"
                        onClick={() => setCustomerCategory('all')}
                        className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                          customerCategory === 'all'
                            ? 'bg-gray-900 text-white dark:bg-gray-200 dark:text-gray-900'
                            : 'bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-200'
                        }`}
                      >
                        全部類別
                      </button>
                      {customerCategories.map((cat) => (
                        <button
                          key={cat}
                          type="button"
                          onClick={() => setCustomerCategory(cat)}
                          className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                            customerCategory === cat
                              ? 'bg-gray-900 text-white dark:bg-gray-200 dark:text-gray-900'
                              : 'bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-200'
                          }`}
                        >
                          {getCustomerNotificationTypeLabel(cat)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="min-h-0 flex-1 overflow-y-auto">
                    {isLoading ? (
                      <div className="p-6 text-center">
                        <div className="mx-auto h-6 w-6 animate-spin rounded-full border-b-2 border-blue-600" />
                        <p className="mt-2 text-xs text-gray-500">載入中…</p>
                      </div>
                    ) : customerFiltered.length === 0 ? (
                      <div className="px-4 py-10 text-center text-gray-500 dark:text-gray-400">
                        <Bell className="mx-auto mb-2 h-10 w-10 opacity-40" />
                        <p className="text-sm font-medium">暫無通知</p>
                        <p className="mt-1 text-xs">目前沒有符合篩選的通知</p>
                      </div>
                    ) : (
                      customerGrouped.map((group) => (
                        <div key={group.label}>
                          <div className="flex items-center justify-between bg-gray-50/80 px-3 py-1.5 dark:bg-gray-900/50">
                            <span className="text-[11px] font-bold text-gray-500">{group.label}</span>
                            <button
                              type="button"
                              onClick={resetCustomerFilters}
                              className="text-[11px] font-semibold text-blue-600 hover:underline"
                            >
                              查看全部
                            </button>
                          </div>
                          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                            {group.notifications.map((notification) => (
                              <li key={notification.id}>
                                <div className="flex gap-1.5 px-2 py-1.5">
                                  <button
                                    type="button"
                                    onClick={() => void handleNotificationClick(notification)}
                                    className="flex min-w-0 flex-1 gap-2 rounded-lg px-1 py-1 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/80"
                                  >
                                    <span className="relative shrink-0">
                                      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-gray-100 to-gray-200 text-sm font-bold text-gray-700 dark:from-gray-600 dark:to-gray-700 dark:text-gray-100">
                                        {(notification.title || '通').charAt(0)}
                                      </span>
                                      <span
                                        className={`absolute -bottom-0.5 -right-0.5 flex h-[18px] w-[18px] items-center justify-center rounded-full border-2 border-white dark:border-gray-800 ${getCustomerNotificationIconBg(notification.type)}`}
                                      >
                                        <CustomerBadgeIcon type={notification.type} />
                                      </span>
                                    </span>
                                    <span className="min-w-0 flex-1">
                                      <span className="line-clamp-2 text-[13px] leading-snug text-gray-900 dark:text-white">
                                        <span className="font-semibold">{notification.title}</span>
                                        {notification.message ? (
                                          <span className="font-normal text-gray-700 dark:text-gray-300">
                                            {' '}
                                            {notification.message}
                                          </span>
                                        ) : null}
                                      </span>
                                      <span className="mt-0.5 block text-[11px] font-medium text-blue-600">
                                        {formatTime(notification.createdAt)}
                                      </span>
                                    </span>
                                  </button>
                                  <div className="flex shrink-0 flex-col items-end gap-0.5 pt-1">
                                    {!notification.isRead && (
                                      <span className="h-2 w-2 rounded-full bg-[#1877F2]" title="未讀" />
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => void deleteNotification(notification.id)}
                                      className="rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-600"
                                      aria-label="移除"
                                    >
                                      <X className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))
                    )}
                  </div>

                  <div className="shrink-0 border-t border-gray-200 p-2 dark:border-gray-700">
                    <button
                      type="button"
                      onClick={() => {
                        setShowDropdown(false);
                        if (currentUser.userType === 'company') {
                          navigate('/company/jobs');
                        } else if (currentUser.userType === 'craftsman') {
                          navigate('/craftsman/notifications');
                        } else {
                          navigate('/customer/notifications');
                        }
                      }}
                      className="w-full rounded-lg py-2.5 text-center text-sm font-semibold text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-gray-700"
                    >
                      {currentUser.userType === 'company'
                        ? '前往案件管理'
                        : '前往通知中心'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="max-h-80 overflow-y-auto">
                    {isLoading ? (
                      <div className="p-4 text-center">
                        <div className="mx-auto h-6 w-6 animate-spin rounded-full border-b-2 border-blue-600" />
                        <p className="mt-2 text-sm text-gray-500">載入中...</p>
                      </div>
                    ) : notifications.length > 0 ? (
                      <div className="divide-y divide-gray-200 dark:divide-gray-700">
                        {notifications.map((notification) => (
                          <motion.div
                            key={notification.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            className={`cursor-pointer p-4 transition-colors hover:bg-gray-50 dark:hover:bg-gray-700 ${
                              !notification.isRead ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                            }`}
                            onClick={() => void handleNotificationClick(notification)}
                          >
                            <div className="flex items-start space-x-3">
                              <div className="mt-1 flex-shrink-0">{getNotificationIcon(notification.type)}</div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center justify-between">
                                  <p
                                    className={`text-sm font-medium ${
                                      !notification.isRead
                                        ? 'text-gray-900 dark:text-white'
                                        : 'text-gray-700 dark:text-gray-300'
                                    }`}
                                  >
                                    {notification.title}
                                    {!notification.isRead && (
                                      <span className="ml-2 inline-block h-2 w-2 rounded-full bg-blue-500" />
                                    )}
                                  </p>
                                  <div className="flex items-center space-x-1">
                                    {!notification.isRead && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          void markAsRead(notification.id);
                                        }}
                                        className="text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                                        title="標記為已讀"
                                      >
                                        <Check className="h-3 w-3" />
                                      </button>
                                    )}
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        void deleteNotification(notification.id);
                                      }}
                                      className="text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                                      title="刪除"
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  </div>
                                </div>
                                <p className="mt-1 line-clamp-2 text-sm text-gray-600 dark:text-gray-400">
                                  {notification.message}
                                </p>
                                <p className="mt-2 text-xs text-gray-500 dark:text-gray-500">
                                  {formatTime(notification.createdAt)}
                                </p>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                        <Bell className="mx-auto mb-4 h-12 w-12 opacity-50" />
                        <p className="text-sm">暫無通知</p>
                      </div>
                    )}
                  </div>

                  {notifications.length > 0 && (
                    <div className="border-t border-gray-200 p-3 dark:border-gray-700">
                      <button
                        type="button"
                        onClick={() => {
                          setShowDropdown(false);
                          if (currentUser.userType === 'company') {
                            navigate('/company/dashboard');
                          } else if (currentUser.userType === 'craftsman') {
                            navigate('/craftsman/notifications');
                          } else if (currentUser.userType === 'customer') {
                            navigate('/customer/notifications');
                          }
                        }}
                        className="w-full py-2 text-center text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        查看全部通知
                      </button>
                    </div>
                  )}
                </>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MessageNotifications;
